/**
 * Modal — overlay dialog primitive.
 *
 * Thin wrapper over Radix Dialog: handles backdrop, focus trap, Escape to
 * close, scroll lock, and portal rendering — so feature code only worries
 * about the dialog content.
 *
 * Visuals come from CSS Modules + design tokens; Radix only provides
 * behavior. The wrapper intentionally exposes a small surface (open,
 * onOpenChange, title for the labelled heading, children for the body) —
 * if you need a custom layout, drop down to Radix Dialog directly.
 *
 * Use `<ConfirmDialog>` for the most common shape (destructive confirm).
 * Use this primitive when you need arbitrary body content.
 */

import { type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { t } from '@/lib/i18n';
import styles from './Modal.module.css';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

const SIZE_CLASS: Record<ModalSize, string | undefined> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
  '2xl': styles.size2xl,
  full: styles.sizeFull,
};

export interface ModalProps {
  /** Controls visibility. */
  open: boolean;
  /** Called when the modal would like to close — Esc, backdrop click, etc. */
  onOpenChange: (open: boolean) => void;
  /** Accessible name for the dialog (rendered as the visible heading). */
  title: ReactNode;
  /** Optional explainer text shown under the title. */
  description?: ReactNode;
  /** Custom-label for the close × button. Defaults to "Close". */
  closeLabel?: string;
  /** Hide the trailing × button (e.g., when the dialog can only be dismissed by an action). */
  hideClose?: boolean;
  /** Body content. */
  children?: ReactNode;
  /** Right-aligned action buttons row (Cancel / Confirm etc.). */
  actions?: ReactNode;
  /**
   * Width preset.
   *  - sm (360px) — short confirms
   *  - md (440px, default) — most modals
   *  - lg (720px) — content previews, longer forms
   *  - xl (960px) — wide content (file/diff viewers)
   *  - 2xl (1200px) — very wide content (data tables, long-form documents)
   *  - full (viewport minus margins) — maximum width content
   */
  size?: ModalSize;
  /**
   * ARIA role for the dialog content. Use "alertdialog" for destructive
   * confirmations that require user acknowledgement. Defaults to "dialog".
   */
  role?: 'dialog' | 'alertdialog';
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  hideClose = false,
  children,
  actions,
  size = 'md',
  role,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          {...(role !== undefined ? { role } : {})}
          className={[styles.content, SIZE_CLASS[size]].filter(Boolean).join(' ')}
        >
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>{title}</Dialog.Title>
            {!hideClose ? (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className={styles.close}
                  aria-label={closeLabel ?? t('Close')}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </Dialog.Close>
            ) : null}
          </div>
          {description ? (
            <Dialog.Description className={styles.description}>{description}</Dialog.Description>
          ) : null}
          {children ? <div className={styles.body}>{children}</div> : null}
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
